import type { LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { useMemo } from 'react';
import { FlagTriangleRight, ListChecks, Radio } from 'lucide-react';

import type {
  KeyRiskSignalsComponent,
  RiskAssessmentComponent,
  SummaryComponent,
  TransactionMetricsComponent,
  TransactionPatternsAnalysisComponent,
} from '@ballerine/business-analysis-schemas';

import { ReportMarkdown } from '../../report-shared/components/ReportMarkdown';
import { FileDetails } from '../components/FileDetails';
import { HighRiskTransactions } from '../components/HighRiskTransactions';
import { KeyRiskSignals } from '../components/KeyRiskSignals';
import { RiskLevelCard } from '../components/RiskLevelCard';
import { StatWidgets } from '../components/StatWidgets';

type TransactionAnalysisSection = {
  id: string;
  title: string;
  Icon?: LucideIcon;
  Component: ReactNode;
  isHidden: boolean;
};

export type TransactionAnalysisData = {
  riskAssessment?: RiskAssessmentComponent | null;
  summary?: SummaryComponent | null;
  transactionMetrics?: TransactionMetricsComponent | null;
  keyRiskSignals?: KeyRiskSignalsComponent | null;
  transactionPatternsAnalysis?: TransactionPatternsAnalysisComponent | null;
  merchantName?: string | null;
  merchantId?: string | null;
  websitesCount?: number;
};

export const useTransactionAnalysisReportSections = (
  data: TransactionAnalysisData,
): { sections: TransactionAnalysisSection[] } => {
  const {
    riskAssessment,
    summary,
    transactionMetrics,
    keyRiskSignals,
    transactionPatternsAnalysis,
    merchantId,
    websitesCount,
  } = data;

  const sections = useMemo(() => {
    const isEmpty =
      !riskAssessment &&
      !summary &&
      !transactionMetrics &&
      !keyRiskSignals &&
      !transactionPatternsAnalysis;

    if (isEmpty) {
      return [];
    }

    const riskLevel = riskAssessment?.riskLevel ?? 'unknown';
    const keyLaunderingSignals = keyRiskSignals?.launderingSignals;
    const riskLevelCardSignals = keyLaunderingSignals?.map(sig => ({
      signal: sig.signal,
      severity: sig.severity,
    }));
    const overallAssessmentText = summary?.text;
    const summaryMetrics = transactionMetrics?.summaryMetrics ?? undefined;
    const avgTransactionValue = transactionMetrics?.avgTransactionValue;
    const processingMccNumber = transactionMetrics?.processingMcc?.mcc || 'unknown';
    const processingMccLabel = transactionMetrics?.processingMcc?.description || 'Unknown';
    const geoDistribution = transactionMetrics?.geoDistribution;

    const primaryMarkets: Array<{ country: string; percentage: number }> = (() => {
      if (geoDistribution?.fullBreakdown && geoDistribution.fullBreakdown.length > 0) {
        return geoDistribution.fullBreakdown;
      }

      if (
        geoDistribution?.primaryMarketCountry &&
        geoDistribution.primaryMarketCountry !== 'unknown'
      ) {
        return [
          {
            country: geoDistribution.primaryMarketCountry,
            percentage: geoDistribution.primaryMarketPercentage,
          },
        ];
      }

      return [];
    })();

    const highRiskTransactions =
      transactionPatternsAnalysis?.flaggedTransactions?.highRiskTransactions;

    const dateRange = transactionMetrics?.dateRange ?? undefined;
    const totalTransactions = transactionMetrics?.numTransactions ?? undefined;

    const allSections = [
      {
        id: 'risk-analysis',
        title: 'Risk Analysis',
        Icon: ListChecks,
        isHidden: false,
        Component: (
          <div className="space-y-8">
            <FileDetails
              dateRange={dateRange}
              websitesCount={websitesCount}
              totalTransactions={totalTransactions}
              merchantId={merchantId ?? undefined}
            />

            <RiskLevelCard level={riskLevel} signals={riskLevelCardSignals} />

            <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-md">
              <div className="space-y-8">
                <h2 className="text-xl font-semibold" style={{ color: '#14203D' }}>
                  Risk Analysis
                </h2>

                <div>
                  <h3 className="font-semibold text-gray-900">Summary</h3>
                  {overallAssessmentText && (
                    <ReportMarkdown>{overallAssessmentText}</ReportMarkdown>
                  )}
                </div>

                {summaryMetrics && (
                  <StatWidgets
                    metrics={summaryMetrics}
                    processingMccNumber={processingMccNumber}
                    processingMccLabel={processingMccLabel}
                    avgTransactionAmount={avgTransactionValue?.amount ?? 0}
                    avgTransactionCurrency={avgTransactionValue?.currency ?? 'USD'}
                    primaryMarkets={primaryMarkets}
                  />
                )}
              </div>
            </div>
          </div>
        ),
      },
      {
        id: 'key-risk-signals',
        title: 'Key Risk Signals',
        Icon: Radio,
        isHidden: keyLaunderingSignals == null,
        Component: (
          <div className="space-y-8 pt-12">
            {keyLaunderingSignals && keyLaunderingSignals.length > 0 && (
              <KeyRiskSignals signals={keyLaunderingSignals} />
            )}
          </div>
        ),
      },
      {
        id: 'flagged-transactions',
        title: 'Flagged Transactions',
        Icon: FlagTriangleRight,
        isHidden: highRiskTransactions == null,
        Component: (
          <div className="space-y-8 pt-12">
            {highRiskTransactions && highRiskTransactions.length > 0 && (
              <HighRiskTransactions transactions={highRiskTransactions} />
            )}
          </div>
        ),
      },
    ] satisfies TransactionAnalysisSection[];

    return allSections.filter(section => !section.isHidden);
  }, [
    riskAssessment,
    summary,
    transactionMetrics,
    keyRiskSignals,
    transactionPatternsAnalysis,
    merchantId,
    websitesCount,
  ]);

  return { sections };
};
